import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { Id } from "@core/domain/value-objects/id.value-object";
import { zIdSchema } from "@core/domain/value-objects/id.value-object";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import { merge } from "es-toolkit";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const ConnectionAlertSchema = z
    .object({
        id: zIdSchema,
        hoursElapsedToTrigger: z.number(),
        notifyAll: z.boolean(),
        stationIds: z.array(z.number()),
        stations: z.record(z.string(), z.string().trim()),
        isEnabled: z.boolean(),
    })
    .brand<"ConnectionAlert">();

export type ConnectionAlertInputProps = z.input<typeof ConnectionAlertSchema>;
export type ConnectionAlertProps = z.output<typeof ConnectionAlertSchema>;

export class ConnectionAlert {
    private constructor(
        private readonly props: Readonly<ConnectionAlertProps>,
    ) {}

    get id(): Id {
        return this.props.id;
    }

    get hoursElapsedToTrigger(): number {
        return this.props.hoursElapsedToTrigger;
    }

    get notifyAll(): boolean {
        return this.props.notifyAll;
    }

    get stationIds(): number[] {
        return this.props.stationIds;
    }

    get stations(): Nullable<Record<string, string>> {
        return this.props.stations;
    }

    get isEnabled(): boolean {
        return this.props.isEnabled;
    }

    static validate(
        props: Readonly<MaybeValidProps<ConnectionAlertInputProps>>,
    ): Result<ConnectionAlertProps, z.ZodError<ConnectionAlertProps>> {
        const result = ConnectionAlertSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<ConnectionAlertInputProps>>,
    ): Result<ConnectionAlertProps, PartialValid<typeof ConnectionAlert>> {
        const result = partialSafeParse(ConnectionAlertSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<ConnectionAlertInputProps>,
    ): Result<ConnectionAlert, z.ZodError<ConnectionAlertProps>> {
        return ConnectionAlert.validate(props).map(
            (validatedProps) => new ConnectionAlert(validatedProps),
        );
    }

    static createWithRecovery(
        props: ConnectionAlertInputProps,
    ): Result<ConnectionAlert, ModelError<typeof ConnectionAlert>> {
        return ConnectionAlert.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<ConnectionAlertProps> = {
                    id: error.validProps.id,
                    hoursElapsedToTrigger: 0,
                    notifyAll: false,
                    stationIds: [],
                    stations: {},
                    isEnabled: false,
                };

                const recoveryProps: Unbrand<ConnectionAlertProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof ConnectionAlert>(
                    new ConnectionAlert(recoveryProps as ConnectionAlertProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new ConnectionAlert(validatedProps));
    }

    toCreateProps(): ConnectionAlertInputProps {
        return {
            id: this.props.id.toCreateProps(),
            hoursElapsedToTrigger: this.props.hoursElapsedToTrigger,
            notifyAll: this.props.notifyAll,
            stationIds: this.props.stationIds,
            stations: this.props.stations,
            isEnabled: this.props.isEnabled,
        };
    }
}

const ConnectionAlertsSchema = z
    .object({
        connectionAlerts: z.array(
            z.custom<ConnectionAlert>((val) => val instanceof ConnectionAlert, {
                error: "Invalid ConnectionAlert instance",
            }),
        ),
        count: z.number().min(0),
    })
    .brand<"ConnectionAlerts">();

export type ConnectionAlertsInputProps = z.input<typeof ConnectionAlertsSchema>;
export type ConnectionAlertsProps = z.output<typeof ConnectionAlertsSchema>;

export class ConnectionAlerts {
    private constructor(
        private readonly props: Readonly<ConnectionAlertsProps>,
    ) {}

    get connectionAlerts(): readonly Readonly<ConnectionAlert>[] {
        return this.props.connectionAlerts;
    }

    get count(): number {
        return this.props.count;
    }

    static empty(): ConnectionAlerts {
        return ConnectionAlerts.create({
            connectionAlerts: [],
            count: 0,
        })._unsafeUnwrap();
    }

    static validate(
        props: Readonly<MaybeValidProps<ConnectionAlertsInputProps>>,
    ): Result<ConnectionAlertsProps, z.ZodError<ConnectionAlertsProps>> {
        const result = ConnectionAlertsSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    static create(
        props: Readonly<ConnectionAlertsInputProps>,
    ): Result<ConnectionAlerts, z.ZodError<ConnectionAlertsProps>> {
        return ConnectionAlerts.validate(props).map(
            (validatedProps) => new ConnectionAlerts(validatedProps),
        );
    }
}

export interface ConnectionAlertUpdate {
    hoursElapsedToTrigger: number;
    notifyAll: boolean;
    stationIds: number[];
    isEnabled: boolean;
}
